<template>
  <v-card data-testid="import_opticalgym_package">
    <v-card-title style="padding: 0px">
      <v-toolbar grey flat>
        <v-btn icon :disabled="importing" @click="$emit('close')" data-testid="import_opticalgym_package_close">
          <v-icon>close</v-icon>
        </v-btn>
        <v-toolbar-title>{{ $t("import_opticalgym_package.title") }}</v-toolbar-title>
      </v-toolbar>
    </v-card-title>

    <v-card-text class="pt-4">
      <v-progress-linear v-if="loading || importing" indeterminate />

      <div v-if="!has_manifest">
        <v-file-input
          v-model="file"
          accept=".opticalgym,application/zip"
          :label="$t('import_opticalgym_package.file_label')"
          :disabled="loading || importing"
          prepend-icon="fas fa-file-import"
          outlined
          data-testid="import_opticalgym_package_file"
        />
      </div>
      <div v-else>
        <div class="d-flex align-center mb-2">
          <div class="text-truncate">{{ file_name }}</div>
          <v-spacer />
          <v-btn
            text
            small
            :disabled="loading || importing"
            @click="change_file"
            data-testid="import_opticalgym_package_change_file"
          >
            {{ $t("import_opticalgym_package.change_file") }}
          </v-btn>
        </div>

        <v-checkbox
          v-if="is_admin"
          v-model="remap_uuids"
          :label="$t('import_opticalgym_package.remap_uuids')"
          :disabled="importing"
          hide-details
          class="mt-0 mb-2"
          data-testid="import_opticalgym_package_remap_uuids"
        />

        <div v-if="sections.length === 0">
          {{ $t("import_opticalgym_package.empty") }}
        </div>
        <div v-else>
          <div v-for="section in sections" :key="section.entity_type">
            <v-subheader>
              <v-icon left small>{{ section.icon }}</v-icon>
              {{ section.title }}
            </v-subheader>
            <v-list>
              <v-list-item
                v-for="item in section.items"
                :key="item.key"
                @click="toggle(item)"
              >
                <v-list-item-action>
                  <v-checkbox
                    :input-value="is_selected(item.key)"
                    :disabled="is_locked(item.key) || importing"
                    hide-details
                    class="mt-0"
                    data-testid="import_opticalgym_package_entity"
                    :data-entity-id="item.id"
                    :data-entity-type="item.entity_type"
                    @click.native.stop
                    @change="toggle(item)"
                  />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>{{ item.title }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.description }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>
        </div>
      </div>
    </v-card-text>

    <v-card-actions>
      <v-spacer />
      <v-btn text :disabled="importing" @click="$emit('close')">
        {{ $t("common.cancel") }}
      </v-btn>
      <v-btn
        color="primary"
        :loading="importing"
        :disabled="!has_manifest || loading || importing || effective_selected_keys.length === 0"
        @click="import_package"
        data-testid="import_opticalgym_package_import"
      >
        {{ $t("common.import") }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import axios from "axios";

const SECTION_TYPES = [
  { entity_type: "grid_set", title_key: "community_sharing.grid_sets", icon: "fas fa-align-justify" },
  { entity_type: "grid_schedule", title_key: "community_sharing.grid_schedules", icon: "fas fa-border-all" },
  { entity_type: "grid_sequence", title_key: "community_sharing.grid_sequences", icon: "mdi-source-branch" },
  { entity_type: "video_exercise", title_key: "community_sharing.video_exercises", icon: "video_library" },
  { entity_type: "reading_text", title_key: "community_sharing.reading_texts", icon: "mdi-book-open-page-variant" },
  { entity_type: "file", title_key: "community_sharing.printable_sheets", icon: "print" },
];

function entity_key(entity_type, id) {
  return `${entity_type}:${id}`;
}

function normalize_manifest_entity(entity) {
  const seen = {};
  const dep_keys = [];
  for (const dep of entity.dependencies || []) {
    const key = entity_key(dep.entity_type, dep.id);
    if (seen[key]) {
      continue;
    }
    seen[key] = true;
    dep_keys.push(key);
  }
  return {
    key: entity_key(entity.entity_type, entity.id),
    entity_type: entity.entity_type,
    id: entity.id,
    title: entity.title || "",
    description: entity.description || "",
    dep_keys,
  };
}

function as_file(value) {
  if (!value) {
    return null;
  }
  if (Array.isArray(value)) {
    return value[0] || null;
  }
  return value;
}

export default {
  props: {
    clinic_id: {
      type: String,
      required: true,
    },
    current_user: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      file: null,
      loading: false,
      importing: false,
      has_manifest: false,
      items: [],
      explicit_selected: [],
      remap_uuids: false,
    };
  },
  computed: {
    is_admin() {
      return !!(this.current_user && this.current_user.is_admin);
    },
    file_object() {
      return as_file(this.file);
    },
    file_name() {
      return this.file_object ? this.file_object.name : "";
    },
    entity_index() {
      const index = {};
      for (const item of this.items) {
        index[item.key] = item;
      }
      return index;
    },
    sections() {
      return SECTION_TYPES.map((section) => ({
        entity_type: section.entity_type,
        icon: section.icon,
        title: this.$t(section.title_key),
        items: this.items
          .filter((item) => item.entity_type === section.entity_type)
          .slice()
          .sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: "base" })),
      })).filter((section) => section.items.length > 0);
    },
    effective_selected_keys() {
      const selected = {};
      const visit = (key) => {
        if (selected[key]) {
          return;
        }
        const item = this.entity_index[key];
        if (!item) {
          return;
        }
        selected[key] = true;
        for (const dep of item.dep_keys) {
          visit(dep);
        }
      };
      for (const key of this.explicit_selected) {
        visit(key);
      }
      return Object.keys(selected);
    },
    locked_set() {
      const locked = {};
      const visit = (key) => {
        if (locked[key]) {
          return;
        }
        const item = this.entity_index[key];
        if (!item) {
          return;
        }
        locked[key] = true;
        for (const dep of item.dep_keys) {
          visit(dep);
        }
      };
      for (const key of this.explicit_selected) {
        const item = this.entity_index[key];
        if (!item) {
          continue;
        }
        for (const dep of item.dep_keys) {
          visit(dep);
        }
      }
      return locked;
    },
    should_remap_uuids() {
      return this.is_admin ? this.remap_uuids : true;
    },
  },
  watch: {
    file() {
      if (this.file_object && !this.has_manifest && !this.loading) {
        this.load_manifest();
      }
    },
  },
  methods: {
    is_selected(key) {
      return this.effective_selected_keys.indexOf(key) !== -1;
    },
    is_locked(key) {
      return !!this.locked_set[key];
    },
    toggle(item) {
      if (this.is_locked(item.key) || this.importing) {
        return;
      }
      const index = this.explicit_selected.indexOf(item.key);
      if (index !== -1) {
        this.explicit_selected.splice(index, 1);
      } else {
        this.explicit_selected.push(item.key);
      }
    },
    change_file() {
      if (this.importing) {
        return;
      }
      this.file = null;
      this.has_manifest = false;
      this.items = [];
      this.explicit_selected = [];
    },
    response_error(response, fallback) {
      return response?.data?.error_message || fallback;
    },
    async load_manifest() {
      const file = this.file_object;
      if (!file) {
        return;
      }

      this.loading = true;
      this.has_manifest = false;
      this.items = [];
      this.explicit_selected = [];

      try {
        const form = new FormData();
        form.append("file", file);
        const response = await axios.post("/api/opticalgym/manifest", form);
        if (response.data.status !== "success") {
          window.messageBus.$emit(
            "error",
            this.response_error(response, this.$t("import_opticalgym_package.import_failed"))
          );
          this.file = null;
          return;
        }

        const entities = response.data.data.entities || [];
        this.items = entities.map(normalize_manifest_entity);
        this.explicit_selected = this.items.map((item) => item.key);
        this.has_manifest = true;
      } catch (error) {
        window.messageBus.$emit("error", error?.message || this.$t("import_opticalgym_package.import_failed"));
        this.file = null;
      } finally {
        this.loading = false;
      }
    },
    async import_package() {
      const file = this.file_object;
      const entities = this.effective_selected_keys
        .map((key) => this.entity_index[key])
        .filter((item) => item)
        .map((item) => ({
          entity_type: item.entity_type,
          id: item.id,
        }));

      if (!file || entities.length === 0) {
        return;
      }

      this.importing = true;
      try {
        const form = new FormData();
        form.append("file", file);
        form.append("clinic_id", this.clinic_id);
        form.append("remap_uuids", this.should_remap_uuids ? "true" : "false");
        form.append("whitelist", JSON.stringify(entities));

        const response = await axios.post("/api/opticalgym/import", form);
        if (response.data.status !== "success") {
          window.messageBus.$emit(
            "error",
            this.response_error(response, this.$t("import_opticalgym_package.import_failed"))
          );
          return;
        }

        const imported = response.data.data.imported || [];
        window.messageBus.$emit(
          "success",
          this.$t("import_opticalgym_package.import_succeeded", { count: imported.length })
        );
        this.$emit("close");
      } catch (error) {
        window.messageBus.$emit("error", error?.message || this.$t("import_opticalgym_package.import_failed"));
      } finally {
        this.importing = false;
      }
    },
  },
};
</script>
